// Maps the live /projects API shape (ProjectDetail) onto the shape the ported
// prototype cards/HUD expect (see data.js PROJECTS). Read-only for this version.
import type { ProjectDetail } from "../types/dashboard";

// Card display shape consumed by cards.js / hud.js
export interface SergaProject {
  id: string;
  title: string;
  status: string; // "Active" | "Paused" | ...
  priority: string; // "High" | "Medium" | "Low"
  due: string | null;
  tag: string;
  glyph: string;
  desc: string;
  next: string;
  // `id` carries the real DynamoDB taskId/artifactId so the SHELL can wire
  // edits to the live API. Cards render `t`/`s`/`title` and ignore the id.
  tasks: { t: string; s: string; id?: string }[];
  artifacts: { id?: string; title: string }[];
}

const STATUS: Record<string, string> = {
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  archived: "Archived",
};
const PRIORITY: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};
const TASK_STATUS: Record<string, string> = {
  todo: "To Do",
  in_progress: "In Progress",
  done: "Done",
  blocked: "Blocked",
};

// Glyph pool (keys from glyphs.js). Cycled by index so each project gets a
// stable, distinct instrument mark until the backend stores real glyph ids.
const GLYPH_POOL = ["cit", "adv", "trn", "prop", "fin"];

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Derive a short code tag (e.g. "CZE-01") from the title until the backend
// provides real tags. Deterministic per position.
function deriveTag(title: string, i: number): string {
  const letters = (title || "PRJ").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3) || "PRJ";
  return `${letters}-${String(i + 1).padStart(2, "0")}`;
}

export function mapProject(d: ProjectDetail, i: number): SergaProject {
  const tasks = (d.tasks || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((t) => ({ t: t.title, s: TASK_STATUS[t.status] || "To Do", id: t.taskId }));

  return {
    id: d.projectId,
    title: d.title,
    status: STATUS[d.status] || cap(d.status),
    priority: PRIORITY[d.priority] || cap(d.priority),
    due: d.deadline || null,
    tag: deriveTag(d.title, i),
    glyph: GLYPH_POOL[i % GLYPH_POOL.length],
    desc: d.description || "",
    next: d.nextAction || "",
    tasks,
    artifacts: (d.artifacts || []).map((a) => ({ id: a.artifactId, title: a.title })),
  };
}
